import React, { useState, useCallback } from 'react';
import { Icon, IconName } from './ui';
import {
  TheaterBeat,
  TheaterState,
  theaterReset,
  theaterBeat,
} from '../lib/riskApi';
import { useTranslation } from 'react-i18next';

interface TheaterProps {
  apiUrl: string;
  /** Called after each beat so the parent Command Center can refresh immediately. */
  onBeat?: () => void;
}

type StepState = 'idle' | 'active' | 'done';

const getBEATS = (t: (key: string) => string): { key: TheaterBeat; label: string; sub: string; icon: IconName }[] => [
  { key: 'injection', label: t('components.commandCenterTheater.promptInjection'), sub: t('components.commandCenterTheater.beatBlocked'), icon: 'bolt' },
  { key: 'dlp', label: t('components.commandCenterTheater.dataExfiltration'), sub: t('components.commandCenterTheater.beatPiiRedacted'), icon: 'shield' },
  { key: 'shadow', label: t('components.commandCenterTheater.shadowAI'), sub: t('components.commandCenterTheater.beatUnsanctioned'), icon: 'search' },
  { key: 'drift', label: t('components.commandCenterTheater.behaviourDrift'), sub: t('components.commandCenterTheater.beatVolumeSpike'), icon: 'view' },
  { key: 'quarantine', label: t('components.commandCenterTheater.quarantine'), sub: t('components.commandCenterTheater.beatContained'), icon: 'lock' },
];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const CommandCenterTheater: React.FC<TheaterProps> = ({ apiUrl, onBeat }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState<Record<TheaterBeat, StepState>>({
    injection: 'idle', dlp: 'idle', shadow: 'idle', drift: 'idle', quarantine: 'idle',
  });
  const [state, setState] = useState<TheaterState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setStep = (k: TheaterBeat, s: StepState) => setSteps((prev) => ({ ...prev, [k]: s }));

  const reset = useCallback(async () => {
    setError(null);
    try {
      const s = await theaterReset(apiUrl);
      setState(s);
      setSteps({ injection: 'idle', dlp: 'idle', shadow: 'idle', drift: 'idle', quarantine: 'idle' });
      onBeat?.();
    } catch (e: any) {
      setError(e.message || 'Reset failed');
    }
  }, [apiUrl, onBeat]);

  const play = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      await theaterReset(apiUrl);
      setSteps({ injection: 'idle', dlp: 'idle', shadow: 'idle', drift: 'idle', quarantine: 'idle' });
      onBeat?.();
      await sleep(600);
      for (const b of getBEATS(t)) {
        setStep(b.key, 'active');
        const res = await theaterBeat(apiUrl, b.key);
        setState({ risk: res.risk, status: res.status });
        setStep(b.key, 'done');
        onBeat?.();
        await sleep(1800);
      }
    } catch (e: any) {
      setError(e.message || 'Demo failed');
    } finally {
      setRunning(false);
    }
  }, [apiUrl, onBeat]);

  const score = state?.risk?.score ?? 0;
  const severity = state?.risk?.severity ?? 'none';
  const quarantined = state?.status === 'suspended';

  return (
    <div className={`cc-theater ${open ? 'open' : ''}`}>
      <button className="cc-theater-toggle" onClick={() => setOpen((v) => !v)}>
        <span className="cc-theater-title">
          <Icon name="bolt" size={16} /> {t('components.commandCenterTheater.attackTheater')}
          <span className="cc-theater-badge">{t('components.commandCenterTheater.demo')}</span>
        </span>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} />
      </button>

      {open && (
        <div className="cc-theater-body">
          <div className="cc-theater-controls">
            <button className="ent-btn primary sm" onClick={play} disabled={running}>
              <Icon name={running ? 'refresh' : 'rocket'} size={15} /> {running ? t('components.commandCenterTheater.running') : t('components.commandCenterTheater.runAttackDemo')}
            </button>
            <button className="ent-btn ghost sm" onClick={reset} disabled={running}>
              <Icon name="refresh" size={15} /> {t('components.commandCenterTheater.reset')}
            </button>
            <div className={`cc-theater-live sev-${severity}`}>
              <span className="cc-theater-live-label">{t('components.commandCenterTheater.orionRisk')}</span>
              <span className="cc-theater-live-score">{score}</span>
              {quarantined && <span className="cc-theater-quar"><Icon name="lock" size={12} /> {t('components.commandCenterTheater.quarantined')}</span>}
            </div>
          </div>

          <div className="cc-theater-steps">
            {getBEATS(t).map((b, i) => (
              <React.Fragment key={b.key}>
                {i > 0 && <div className={`cc-step-link ${steps[b.key] !== 'idle' ? 'lit' : ''}`} />}
                <div className={`cc-step ${steps[b.key]} ${b.key === 'quarantine' ? 'climax' : ''}`}>
                  <span className="cc-step-icon">
                    <Icon name={steps[b.key] === 'done' ? 'check' : b.icon} size={15} />
                  </span>
                  <div className="cc-step-text">
                    <span className="cc-step-label">{b.label}</span>
                    <span className="cc-step-sub">{b.sub}</span>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div className="cc-error" style={{ margin: '10px 0 0' }}>
              <Icon name="warning" size={16} /> {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommandCenterTheater;
